// src/components/ui/animated/AnimatedArrow.tsx
import { motion, AnimatePresence } from "framer-motion";
import type { CSSProperties } from "react";

// ── Types ──────────────────────────────────────────────────────────────────

export type ArrowDirection = "left" | "right" | "up" | "down";
export type ArrowSize = "sm" | "md" | "lg";
export type ArrowVariant = "solid" | "glow" | "minimal";

export interface AnimatedArrowProps {
  /** Controls visibility + triggers the draw animation */
  visible: boolean;
  /** Which direction the arrowhead points */
  direction?: ArrowDirection;
  /** sm = 48px, md = 72px, lg = 100px shaft length */
  size?: ArrowSize;
  /** Visual style variant */
  variant?: ArrowVariant;
  /** Any valid CSS color */
  color?: string;
  /** Optional floating hint label rendered above/beside arrow */
  label?: string;
  /** Delay before draw animation begins (seconds) */
  delay?: number;
  /** Extra Tailwind classes on the wrapper */
  className?: string;
  /** Inline styles for precise positioning */
  style?: CSSProperties;
}

// ── Dimension map ──────────────────────────────────────────────────────────

const SIZE: Record<ArrowSize, { shaft: number; head: number; stroke: number }> =
  {
    sm: { shaft: 48, head: 10, stroke: 1.8 },
    md: { shaft: 72, head: 14, stroke: 2.2 },
    lg: { shaft: 100, head: 18, stroke: 2.8 },
  };

// ── Geometry builder ───────────────────────────────────────────────────────
// Returns SVG viewBox dims + shaft/head paths oriented to `direction`.
// Coordinate system: shaft runs along the primary axis, head at the far end.

function buildGeometry(direction: ArrowDirection, size: ArrowSize) {
  const { shaft, head, stroke } = SIZE[size];
  const pad = head + stroke; // padding so arrowhead is never clipped
  const total = shaft + pad * 2;

  // Horizontal arrow pointing RIGHT — the canonical form.
  // Other directions are handled by an SVG `transform` rotate.
  const shaftPath = `M ${pad} ${total / 2} L ${pad + shaft} ${total / 2}`;
  const headPath = `M ${pad + shaft - head} ${total / 2 - head} 
                     L ${pad + shaft} ${total / 2} 
                     L ${pad + shaft - head} ${total / 2 + head}`;

  const rotationMap: Record<ArrowDirection, number> = {
    right: 0,
    left: 180,
    down: 90,
    up: -90,
  };

  return {
    viewBox: `0 0 ${total} ${total}`,
    w: total,
    h: total,
    shaftPath,
    headPath,
    rotate: rotationMap[direction],
    cx: total / 2,
    cy: total / 2,
    stroke,
    head,
  };
}

// ── Tip pulse position ─────────────────────────────────────────────────────

function tipOffset(
  direction: ArrowDirection,
  size: ArrowSize,
): { x: number; y: number } {
  const { shaft, head } = SIZE[size];
  const pad = head + SIZE[size].stroke;
  const axis = pad + shaft;
  const c = (shaft + pad * 2) / 2;
  const map: Record<ArrowDirection, { x: number; y: number }> = {
    right: { x: axis, y: c },
    left: { x: shaft + pad * 2 - axis, y: c },
    down: { x: c, y: axis },
    up: { x: c, y: shaft + pad * 2 - axis },
  };
  return map[direction];
}

// ── Component ──────────────────────────────────────────────────────────────

export const AnimatedArrow = ({
  visible,
  direction = "right",
  size = "md",
  variant = "glow",
  color = "#48A7FF",
  label,
  delay = 0.1,
  className = "",
  style,
}: AnimatedArrowProps) => {
  const geo = buildGeometry(direction, size);
  const tip = tipOffset(direction, size);

  const glowFilter =
    variant === "glow" ? `drop-shadow(0 0 6px ${color}99)` : "none";

  const strokeOpacity = variant === "minimal" ? 0.55 : 1;
  const labelSide = direction === "left" ? "right" : "left";

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className={`pointer-events-none select-none flex flex-col items-center ${className}`}
          style={style}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.2 } }}
          transition={{ duration: 0.15, delay }}
        >
          {/* ── Floating label ─────────────────────────────────────────── */}
          {label && (
            <motion.span
              className="text-[11px] font-semibold tracking-widest uppercase mb-1 whitespace-nowrap"
              style={{ color, textAlign: labelSide }}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 0.85, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{
                delay: delay + 0.35,
                duration: 0.3,
                ease: "easeOut",
              }}
            >
              {label}
            </motion.span>
          )}

          {/* ── SVG arrow ──────────────────────────────────────────────── */}
          <svg
            width={geo.w}
            height={geo.h}
            viewBox={geo.viewBox}
            fill="none"
            style={{ filter: glowFilter, overflow: "visible" }}
            aria-hidden="true"
          >
            <g transform={`rotate(${geo.rotate}, ${geo.cx}, ${geo.cy})`}>
              {/* Shaft — drawn via pathLength */}
              <motion.path
                d={geo.shaftPath}
                stroke={color}
                strokeWidth={geo.stroke}
                strokeLinecap="round"
                strokeOpacity={strokeOpacity}
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{
                  delay: delay + 0.05,
                  duration: 0.45,
                  ease: [0.25, 0.46, 0.45, 0.94],
                }}
              />

              {/* Arrowhead — draws slightly after shaft */}
              <motion.path
                d={geo.headPath}
                stroke={color}
                strokeWidth={geo.stroke}
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                strokeOpacity={strokeOpacity}
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{
                  delay: delay + 0.42,
                  duration: 0.28,
                  ease: "easeOut",
                }}
              />
            </g>

            {/* Tip pulse dot — bounces along the arrow axis after draw */}
            <motion.circle
              cx={tip.x}
              cy={tip.y}
              r={geo.stroke * 1.4}
              fill={color}
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: [0, 1, 0.7, 1],
                scale: [0, 1.4, 0.9, 1.2],
              }}
              transition={{
                delay: delay + 0.72,
                duration: 0.5,
                ease: "easeOut",
                times: [0, 0.4, 0.7, 1],
              }}
            />
          </svg>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
